
import { ReactNode, useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { SyncState } from "@/utils/permissions/PermissionManager";
import { Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

interface AuthGuardProps {
  children: ReactNode;
  requiredRole?: string;
  requiredPermission?: string;
  redirectPath?: string;
}

// Cache for permission check results
const permissionCheckCache = new Map<string, {
  result: boolean,
  timestamp: number
}>();

// Cache TTL in milliseconds (5 minutes)
const PERMISSION_CACHE_TTL = 5 * 60 * 1000;

// Longer debounce time for permission checks
const PERMISSION_CHECK_DEBOUNCE = 500; // 500ms instead of 100ms

export function AuthGuard({ 
  children, 
  requiredRole,
  requiredPermission,
  redirectPath = "/auth"
}: AuthGuardProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { hasRole, hasPermission, syncState } = usePermissions();
  const [permissionsChecked, setPermissionsChecked] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use refs to prevent redundant checks
  const checkingPermissionsRef = useRef(false);
  const permissionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckedPathRef = useRef<string>(location.pathname);
  
  // Generate a cache key for current permission requirements
  const getCacheKey = useCallback((userId?: string) => {
    return `${userId || 'anonymous'}_${requiredRole || 'no-role'}_${requiredPermission || 'no-permission'}`;
  }, [requiredRole, requiredPermission]);
  
  // Check permissions with caching
  const checkPermissions = useCallback(() => {
    if (!isAuthenticated || !user) return false;
    
    const cacheKey = getCacheKey(user.id);
    const cachedResult = permissionCheckCache.get(cacheKey);
    
    // Use cached result if available and fresh
    if (cachedResult && (Date.now() - cachedResult.timestamp < PERMISSION_CACHE_TTL)) {
      return cachedResult.result;
    }
    
    let hasRequiredAccess = true;
    
    // Check for required role
    if (requiredRole && !hasRole(requiredRole as any)) {
      console.log(`User lacks required role: ${requiredRole}`);
      hasRequiredAccess = false;
    }
    
    // Check for required permission
    if (requiredPermission && !hasPermission(requiredPermission)) {
      console.log(`User lacks required permission: ${requiredPermission}`);
      hasRequiredAccess = false;
    }
    
    // Cache the result
    permissionCheckCache.set(cacheKey, {
      result: hasRequiredAccess,
      timestamp: Date.now()
    });
    
    return hasRequiredAccess;
  }, [isAuthenticated, user, requiredRole, requiredPermission, hasRole, hasPermission, getCacheKey]);
  
  // Clear any timeouts on unmount
  useEffect(() => {
    return () => {
      if (permissionsTimeoutRef.current) {
        clearTimeout(permissionsTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Don't do anything until authentication is loaded
    if (isLoading) return;
    
    // If not authenticated, redirect to auth page
    if (!isAuthenticated) {
      navigate(redirectPath, { 
        state: { from: location.pathname, ...location.state } 
      });
      return;
    }
    
    // Skip if path hasn't changed and permissions already checked
    if (lastCheckedPathRef.current === location.pathname && permissionsChecked) {
      return;
    }
    
    // Update last checked path
    lastCheckedPathRef.current = location.pathname;
    
    // Wait for permissions to be synced
    if (syncState === SyncState.SYNCING) {
      return;
    }
    
    // Prevent multiple simultaneous permission checks
    if (checkingPermissionsRef.current) {
      return;
    }
    
    // Set flag to prevent multiple checks
    checkingPermissionsRef.current = true;
    
    // Debounce permission checks to prevent unnecessary redirects
    if (permissionsTimeoutRef.current) {
      clearTimeout(permissionsTimeoutRef.current);
    }
    
    permissionsTimeoutRef.current = setTimeout(() => {
      // Check if component is still mounted (via a ref check)
      const hasRequiredAccess = checkPermissions();
      
      // If lacks permissions, show denial screen or redirect
      if (!hasRequiredAccess) {
        if (location.pathname.includes('/blog/preview')) {
          // For blog preview routes, show permission denied
          setPermissionDenied(true);
        } else {
          // For other routes, redirect to dashboard
          navigate("/dashboard");
        }
        return;
      }
      
      // Mark permissions as checked
      setPermissionsChecked(true);
      checkingPermissionsRef.current = false;
    }, PERMISSION_CHECK_DEBOUNCE);
  }, [
    isAuthenticated, 
    isLoading, 
    navigate, 
    location.pathname, 
    redirectPath, 
    syncState,
    checkPermissions
  ]);

  // If permission is denied, show denied screen
  if (permissionDenied) {
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="flex flex-col items-center">
            <div className="rounded-full bg-red-100 p-3 mb-4">
              <ShieldAlert className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Permission Denied</h2>
            <p className="text-slate-600 mb-6">
              You don't have the required permissions to access this content. 
              {requiredPermission && (
                <span className="block mt-2">
                  Required permission: <code className="bg-slate-100 px-2 py-1 rounded">{requiredPermission}</code>
                </span>
              )}
            </p>
            <Button 
              onClick={() => navigate("/dashboard")}
              className="flex items-center"
            >
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while checking authentication or permissions
  if (isLoading || (isAuthenticated && syncState === SyncState.SYNCING)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="flex flex-col items-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="mt-2 text-sm text-slate-600">
              {isLoading ? "Verifying authentication..." : "Checking permissions..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If authenticated and permissions have been checked, render children
  return (isAuthenticated && permissionsChecked) ? <>{children}</> : null;
}
